import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Save } from "lucide-react";
import { api } from "@/lib/api";

const ALLOW = [
  ["texts_per_door", "Manager & system texts", "segments per door / month"],
  ["voice_minutes_per_door", "AI voice", "minutes per door / month"],
  ["copilot_questions", "Copilot", "questions per community / month"],
  ["onboarding_per_resident", "Onboarding", "segments per resident"],
];
const RATES = [
  ["text", "Text segment", "per segment"],
  ["voice_minute", "Voice minute", "per minute"],
  ["copilot_question", "Copilot question", "per question"],
  ["onboarding_text", "Onboarding segment", "per segment"],
];

const Field = ({ id, label, hint, value, onChange, prefix }) => (
  <label className="block" data-testid={`allow-field-${id}`}>
    <span className="text-xs font-semibold text-ink/70">{label}</span>
    <div className="relative mt-1">
      {prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink/50">{prefix}</span>}
      <input data-testid={`allow-input-${id}`} type="number" min="0" step="any" value={value ?? ""} placeholder="Unlimited"
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border border-sand-200 ${prefix ? "pl-7" : "pl-3"} pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30`} />
    </div>
    <span className="text-[11px] text-ink/45">{hint}</span>
  </label>
);

// Super-admin: the platform's included allowances + overage rates (the numbers on the rate card).
export function AllowanceDefaults() {
  const [al, setAl] = useState(null);
  const [rates, setRates] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/usage/allowances").then((r) => { setAl(r.data.allowances); setRates(r.data.overage_rates); }).catch(() => {}); }, []);
  if (!al) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/usage/allowances", { allowances: al, overage_rates: rates });
      setAl(r.data.allowances); setRates(r.data.overage_rates); toast.success("Allowances saved");
    } catch { toast.error("Couldn't save allowances"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="allowance-defaults">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0"><Gauge className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} /></div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading font-bold text-lg">Included allowances &amp; overage rates</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Platform defaults every community starts with. Allowances pool across a community&rsquo;s doors; the Unlimited Voice add-on removes the minute allowance. Blank = unlimited.</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {ALLOW.map(([k, l, h]) => <Field key={k} id={k} label={l} hint={h} value={al[k]} onChange={(v) => setAl({ ...al, [k]: v === "" ? null : v })} />)}
      </div>
      <div className="mt-4 text-xs font-bold uppercase tracking-wider text-ink/40">Overage rates</div>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {RATES.map(([k, l, h]) => <Field key={k} id={k} label={l} hint={h} prefix="$" value={rates[k]} onChange={(v) => setRates({ ...rates, [k]: v })} />)}
      </div>
      <div className="mt-4 flex justify-end">
        <button data-testid="allow-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50"><Save className="h-4 w-4" /> Save</button>
      </div>
    </div>
  );
}
